
import { Component, Prop, Vue } from 'bits/vue'
import OzTextButton, { OzTextButtonColorScheme, OzTextButtonHrefMode } from 'library/v4/components/OzTextButton.vue'
import tinykeys from 'tinykeys'

interface OzTabsWithSeparatorButtonProps {
  text: string
  key: string
  ariaControls?: string
  href?: string
  hrefMode?: OzTextButtonHrefMode
}

enum OzTabsWithSeparatorAccessibilityRole {
  Tabs = 'Tabs',
  Navigation = 'Navigation',
}

enum OzTabsWithSeparatorSizePreset {
  H42px = 'H42px',
  H59px = 'H59px',
}

/**
 * Orizuru Tabs (Dashed)
 */

@Component({ components: { OzTextButton } })
export default class OzTabsWithSeparator extends Vue {
  OzTextButtonColorScheme = OzTextButtonColorScheme
  SizePreset = OzTabsWithSeparatorSizePreset

  $refs!: {
    list: HTMLDivElement
  }

  /**
   * Accommodates the elements and attributes according to the use.
   * If `Tabs`, the component will be structured according to the ARIA tab list specification.
   * If `Navigation`, the component will be structured according to the ARIA navigation specification.
   */
  @Prop({ default: OzTabsWithSeparatorAccessibilityRole.Tabs })
  accessibilityRole!: OzTabsWithSeparatorAccessibilityRole

  /**
   * Label corresponding to the tab list.
   */
  @Prop({ required: true }) ariaLabel!: string

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: boolean | 'auto'

  /**
   * Key used to detect the current tab.
   */
  @Prop({ required: true }) currentTabKey!: string

  /**
   * Tabs to display.
   */
  @Prop({ required: true }) tabs!: OzTabsWithSeparatorButtonProps[]

  /**
   * Classes applied to the tabs container.
   */
  @Prop({ required: false }) tabsContainerClasses?: string

  /**
   * Applies preset sizes/shapes.
   * They are named with pixel values for developer ergonomics -- those are the values you see in the mockups.
   * Technically, the pixel value could vary depending on the size of `rem`.
   */
  @Prop({ default: OzTabsWithSeparatorSizePreset.H42px }) sizePreset!: OzTabsWithSeparatorSizePreset

  removeKeyboardShortcutsListener: () => void = () => {}

  get highlightedElement(): HTMLElement | null {
    const ref = this.$refs[this.currentTabKey]
    return ref ? ref[0] : null
  }

  get wrapperElement(): string {
    return this.isUsingTabsAccessibilityRole ? 'div' : 'nav'
  }

  get wrapperAttributes(): Record<string, any> {
    return this.isUsingTabsAccessibilityRole ? {} : { 'aria-label': this.ariaLabel }
  }

  get listAttributes(): Record<string, any> {
    return this.isUsingTabsAccessibilityRole ? { 'aria-label': this.ariaLabel } : {}
  }

  get listRole(): string {
    return this.isUsingTabsAccessibilityRole ? 'tablist' : 'list'
  }

  get listItemRole(): string {
    return this.isUsingTabsAccessibilityRole ? 'presentation' : 'listitem'
  }

  get isUsingTabsAccessibilityRole(): boolean {
    return this.accessibilityRole === OzTabsWithSeparatorAccessibilityRole.Tabs
  }

  computeTabAttributes(tab: OzTabsWithSeparatorButtonProps): Record<string, any> {
    return this.isUsingTabsAccessibilityRole
      ? {
          id: tab.key,
          role: 'tab',
          'aria-controls': tab.ariaControls,
          'aria-selected': this.currentTabKey === tab.key ? 'true' : 'false',
          tabindex: this.currentTabKey === tab.key ? 0 : -1,
        }
      : {
          role: tab.href ? 'link' : 'button',
        }
  }

  /**
   * Focus the first tab.
   */
  focusFirstTab(): void {
    if (!this.tabs.length) return
    this.$refs[this.tabs[0].key][0]?.firstChild?.focus()
  }

  /**
   * Focus the last tab.
   */
  focusLastTab(): void {
    if (!this.tabs.length) return
    this.$refs[this.tabs[this.tabs.length - 1].key][0]?.firstChild?.focus()
  }

  /**
   * When pressing "Home", focus the first tab.
   */
  handleHomeKey(event: KeyboardEvent): void {
    event.preventDefault()
    this.focusFirstTab()
  }

  /**
   * When pressing "End", focus the last tab.
   */
  handleEndKey(event: KeyboardEvent): void {
    event.preventDefault()
    this.focusLastTab()
  }

  /**
   * When pressing the left arrow, focus the previous tab.
   * If on first tab, focus the last tab.
   */
  handleArrowLeft(event: KeyboardEvent): void {
    const target = event.target as HTMLElement
    const wrapper = target.parentElement
    // Skip the separator
    if (wrapper?.previousElementSibling?.previousElementSibling) {
      const previousSibling = wrapper.previousElementSibling.previousElementSibling.firstChild as HTMLElement
      previousSibling?.focus()
    } else {
      this.focusLastTab()
    }
  }

  /**
   * When pressing the right arrow, focus the next tab.
   * If on last tab, focus the first tab.
   */
  handleArrowRight(event: KeyboardEvent): void {
    const target = event.target as HTMLElement
    const wrapper = target.parentElement
    // Skip the separator
    if (wrapper?.nextElementSibling?.nextElementSibling) {
      const nextSibling = wrapper.nextElementSibling.nextElementSibling.firstChild as HTMLElement
      nextSibling?.focus()
    } else {
      this.focusFirstTab()
    }
  }

  mounted(): void {
    this.removeKeyboardShortcutsListener = tinykeys(this.$refs.list as HTMLElement, {
      Home: this.handleHomeKey,
      End: this.handleEndKey,
      ArrowLeft: this.handleArrowLeft,
      ArrowRight: this.handleArrowRight,
    })
  }

  beforeDestroy(): void {
    this.removeKeyboardShortcutsListener()
  }
}

export {
  OzTabsWithSeparatorAccessibilityRole,
  OzTabsWithSeparatorButtonProps,
  OzTextButtonHrefMode as OzTabsWithSeparatorHrefMode,
  OzTabsWithSeparatorSizePreset,
}
